import { countText } from "./franchisorListModel.ts";
// CRB8: actual payer-scoped usage. The server owns people, scopes, totals and edit rights.
export interface ReportAiBillingView {
  month: string;
  closed: boolean;
  totalCents: number;
  totalLabel?: string;
  people: Array<{
    personKey: string;
    name: string;
    customerName: string;
    acknowledgedAt: string;
    requests: number;
    totalTokens: string;
    amountCents: number;
    capDollars?: string | null;
    reservedCents?: number;
  }>;
  caps: Array<{
    scope: "account" | "member" | "location";
    scopeId: string;
    name: string;
    capDollars: string | null;
    usedCents: number | null;
    reservedCents: number | null;
    editable: boolean;
  }>;
  months: Array<{ month: string; closed: boolean }>;
  canExport: boolean;
}
interface BillingReply<T> {
  status: number;
  body: T | null;
}
export interface ReportAiBillingHost {
  read(month?: string): Promise<BillingReply<ReportAiBillingView & { message?: string }>>;
  saveCap(input: {
    scope: "account" | "member" | "location";
    scopeId: string;
    capDollars: string | null;
  }): Promise<BillingReply<{ message?: string }>>;
  export?(month: string): Promise<void>;
}
function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const result = document.createElement(tag);
  if (text !== undefined) result.textContent = text;
  return result;
}
function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
}
/** FRBILL2: the franchisor Billing page's plain words for this view (console layout only). */
export const AI_SPENDING_NOTE =
  "What your team's AI report drafting costs this month; Snip & Sip bills it with your subscription.";
export const AI_SPENDING_CAP_NOTE =
  "AI drafting stops for the rest of the month when this is reached. Leave blank for no cap.";
export const AI_LIMITS_LINE =
  "Person and location limits both apply. The first reached stops AI requests for the month.";

export function aiDollarInput(
  value: string | null,
  label: string,
): { field: HTMLElement; input: HTMLInputElement } {
  const field = node("label");
  field.className = "cr-ai-dollar-input";
  const affix = node("span", "$");
  affix.setAttribute("aria-hidden", "true");
  const input = node("input");
  input.type = "text";
  input.inputMode = "numeric";
  input.pattern = "[0-9]*";
  input.value = value ?? "";
  input.setAttribute("aria-label", label);
  field.append(affix, input);
  return { field, input };
}

export function aiLimitWords(value: string | null): string {
  return value === null ? "No limit" : `$${value} per calendar month`;
}
function monthText(month: string) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return "-";
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}
function dateText(value: string) {
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime())
    ? parsed.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      })
    : "-";
}
export function createReportAiBilling(
  hostElement: HTMLElement,
  host: ReportAiBillingHost,
  options: { admin?: boolean } = {},
): { load(month?: string): Promise<void>; reset(): void } {
  const consoleLayout = hostElement.classList.contains("fr-ai-billing-layout");
  // ADMINBILL1: Snip & Sip Admin's read-only view of one customer: a per location table, then people in a scroll region.
  const adminLayout = options.admin === true;
  const styled = consoleLayout || adminLayout;
  const viewName = consoleLayout ? "AI spending" : "AI usage";
  const viewHeading = (): HTMLHeadingElement => node(consoleLayout ? "h4" : "h3", viewName);
  const card = node("section");
  card.className = "so-detail-card cr-ai-billing";
  card.hidden = true;
  hostElement.append(card);
  let generation = 0;
  let selectedMonth: string | undefined;
  let detailsOpen = false;
  function reset() {
    generation += 1;
    selectedMonth = undefined;
    detailsOpen = false;
    card.replaceChildren();
    card.hidden = true;
  }
  async function load(month?: string) {
    const previousDetails = card.querySelector<HTMLDetailsElement>(".cr-ai-spending-details");
    if (previousDetails !== null) detailsOpen = previousDetails.open;
    const expected = ++generation;
    selectedMonth = month;
    card.hidden = false;
    card.replaceChildren(viewHeading());
    const state = node("p", "Loading");
    state.setAttribute("role", "status");
    card.append(state);
    try {
      const reply = await host.read(month);
      if (expected !== generation) return;
      if (reply.status !== 200 || reply.body === null) {
        state.textContent = reply.body?.message ?? `${viewName} is unavailable.`;
        const retry = node("button", "Refresh");
        retry.type = "button";
        if (consoleLayout) retry.className = "btn-secondary";
        retry.onclick = () => void load(selectedMonth);
        card.append(retry);
        return;
      }
      selectedMonth = reply.body.month;
      draw(reply.body, expected);
    } catch {
      if (expected === generation) state.textContent = `${viewName} is unavailable.`;
    }
  }
  function drawAdmin(data: ReportAiBillingView) {
    const period = `${monthText(data.month)}${data.closed ? "" : ", month to date"}`;
    const account = data.caps.find((entry) => entry.scope === "account");
    const summary = node("p");
    summary.className = "so-ai-summary";
    summary.dataset["testid"] = "customer-ai-summary";
    summary.append(
      node("span", `${data.totalLabel ?? "Customer total"}, ${period}: `),
      node("strong", money(data.totalCents)),
    );
    if (!data.closed && account !== undefined)
      summary.append(node("span", `. Limit: ${aiLimitWords(account.capDollars)}.`));
    card.append(summary);
    const scrollRegion = (label: string, content: HTMLElement) => {
      const scroll = node("div");
      scroll.className = "so-ai-scroll";
      scroll.tabIndex = 0;
      scroll.setAttribute("role", "region");
      scroll.setAttribute("aria-label", label);
      scroll.append(content);
      card.append(scroll);
    };
    const where = data.caps.filter((entry) => entry.scope !== "account");
    if (!data.closed && where.length > 0) {
      const whereTable = node("table");
      whereTable.className = "so-table so-ai-table";
      whereTable.dataset["testid"] = "customer-ai-locations";
      const head = node("thead");
      const whereHead = node("tr");
      for (const [index, label] of ["Location", "Used", "Pending", "Limit"].entries()) {
        const th = node("th", label);
        th.scope = "col";
        if (index === 1 || index === 2) th.className = "cr-ai-number";
        whereHead.append(th);
      }
      head.append(whereHead);
      const body = node("tbody");
      for (const cap of where) {
        const row = node("tr");
        const name = node("th", cap.scope === "member" ? `${cap.name} (franchisee)` : cap.name);
        name.scope = "row";
        const used = node("td", cap.usedCents === null ? "Not available" : money(cap.usedCents));
        const pending = node("td", cap.reservedCents === null ? "Not available" : money(cap.reservedCents));
        used.className = pending.className = "cr-ai-number";
        row.append(name, used, pending, node("td", aiLimitWords(cap.capDollars)));
        body.append(row);
      }
      whereTable.append(head, body);
      scrollRegion("AI usage by location", whereTable);
    }
    if (data.people.length === 0) {
      const empty = node(
        "p",
        data.totalCents === 0
          ? `No AI usage in ${monthText(data.month)} yet.`
          : "No people to show for this month.",
      );
      empty.className = "so-ai-empty";
      card.append(empty);
      return;
    }
    const table = node("table");
    table.className = "so-table so-ai-table so-ai-people";
    table.dataset["testid"] = "customer-ai-people";
    const head = node("thead");
    const headRow = node("tr");
    // The Customer column only helps when the people belong to more than one customer (a franchisor's network).
    const showCustomer = new Set(data.people.map((person) => person.customerName)).size > 1;
    const columns: Array<{ label: string; number: boolean; text: (p: ReportAiBillingView["people"][number]) => string }> = [
      { label: "Person", number: false, text: (p) => p.name },
      ...(showCustomer ? [{ label: "Customer", number: false, text: (p: ReportAiBillingView["people"][number]) => p.customerName }] : []),
      { label: "Accepted AI charges", number: false, text: (p) => dateText(p.acknowledgedAt) },
      { label: "Requests", number: true, text: (p) => String(p.requests) },
      { label: "Tokens", number: true, text: (p) => p.totalTokens },
      {
        label: "Monthly limit",
        number: false,
        text: (p) => (p.capDollars === undefined ? "Not available" : aiLimitWords(p.capDollars)),
      },
      { label: data.closed ? "Spend" : "Spend to date", number: true, text: (p) => money(p.amountCents) },
      { label: "Pending", number: true, text: (p) => (p.reservedCents === undefined ? "-" : money(p.reservedCents)) },
    ];
    for (const column of columns) {
      const th = node("th", column.label);
      th.scope = "col";
      if (column.number) th.className = "cr-ai-number";
      headRow.append(th);
    }
    head.append(headRow);
    const body = node("tbody");
    for (const person of data.people) {
      const row = node("tr");
      for (const column of columns) {
        const cell = node("td", column.text(person));
        if (column.number) cell.className = "cr-ai-number";
        row.append(cell);
      }
      body.append(row);
    }
    const foot = node("tfoot");
    const total = node("tr");
    const label = node(
      "th",
      `${data.totalLabel ?? "Customer total"}, ${countText(data.people.length, "person", "people")}`,
    );
    label.colSpan = columns.length - 2;
    label.scope = "row";
    const totalAmount = node("td", money(data.totalCents));
    totalAmount.className = "cr-ai-number";
    total.append(label, totalAmount, node("td", ""));
    foot.append(total);
    table.append(head, body, foot);
    scrollRegion("AI usage by person", table);
  }
  function draw(data: ReportAiBillingView, expected: number) {
    const title = viewHeading();
    card.replaceChildren(title);
    const toolbar = node("div");
    toolbar.className = "so-actions cr-ai-billing-toolbar";
    const picker = node("select");
    picker.setAttribute("aria-label", "AI usage month");
    const months = data.months.some((entry) => entry.month === data.month)
      ? data.months
      : [{ month: data.month, closed: data.closed }, ...data.months];
    for (const entry of months) {
      const option = node("option", `${monthText(entry.month)}${entry.closed ? "" : ", to date"}`);
      option.value = entry.month;
      picker.append(option);
    }
    picker.value = data.month;
    picker.onchange = () => void load(picker.value);
    if (styled) {
      picker.className = "field-input";
      const monthLabel = node("label", "Month");
      monthLabel.className = "cr-ai-billing-month";
      monthLabel.append(picker);
      toolbar.append(monthLabel);
    } else toolbar.append(picker);
    const refresh = node("button", "Refresh");
    refresh.type = "button";
    if (styled) refresh.className = "btn-secondary";
    refresh.onclick = () => void load(data.month);
    toolbar.append(refresh);
    if (data.canExport && host.export !== undefined) {
      const exportButton = node("button", "Export");
      exportButton.type = "button";
      if (styled) exportButton.className = "btn-secondary";
      exportButton.onclick = () => {
        exportButton.disabled = true;
        void (host.export?.(data.month) ?? Promise.resolve())
          .catch(() => {
            exportButton.textContent = "Try export again";
          })
          .finally(() => {
            exportButton.disabled = false;
          });
      };
      toolbar.append(exportButton);
    }
    if (styled) {
      const header = node("div");
      header.className = "cr-ai-billing-heading";
      header.append(title, toolbar);
      card.append(header);
      const note = node(
        "p",
        adminLayout
          ? "What this customer's AI report drafting cost, by location. Limits are set by the customer and shown here read only."
          : AI_SPENDING_NOTE,
      );
      note.className = "cr-ai-billing-note";
      card.append(note);
    } else card.append(toolbar);
    if (adminLayout) {
      drawAdmin(data);
      return;
    }
    let detailHost: HTMLElement = card;
    if (consoleLayout) {
      const summary = node("div");
      summary.className = "cr-ai-spending-summary";
      summary.append(
        node("span", `${data.totalLabel ?? "Account total"}, ${countText(data.people.length, "person", "people")}`),
        node("strong", money(data.totalCents)),
        node("span", data.closed ? "Closed month" : "Month to date"),
      );
      summary.lastElementChild?.classList.add("cr-ai-spending-period");
      const details = node("details");
      details.className = "cr-ai-spending-details";
      details.open = detailsOpen;
      details.append(node("summary", data.closed ? "Details" : "Details and monthly caps"));
      details.addEventListener("toggle", () => {
        if (expected === generation) detailsOpen = details.open;
      });
      // ADMINUX1 (item 08, rule 8): the cap sits in the summary with its own Edit, so the cap is not hidden behind the table.
      const accountCap = data.caps.find((entry) => entry.scope === "account");
      if (!data.closed && accountCap !== undefined) {
        const capLine = node(
          "span",
          `Monthly cap: ${accountCap.capDollars === null ? "No cap" : `$${accountCap.capDollars} per calendar month`}${accountCap.usedCents === null ? "" : `, ${money(accountCap.usedCents)} used`}`,
        );
        capLine.className = "cr-ai-spending-cap";
        capLine.dataset["testid"] = "fr-ai-cap-summary";
        summary.append(capLine);
      }
      if (!data.closed && data.caps.some((entry) => entry.editable)) {
        const edit = node("button", "Edit cap");
        edit.type = "button";
        edit.className = "btn-secondary cr-ai-edit-cap";
        edit.dataset["testid"] = "fr-ai-cap-edit";
        edit.onclick = () => {
          details.open = true;
          detailsOpen = true;
          details.querySelector<HTMLInputElement>(".cr-ai-cap-form input")?.focus();
        };
        summary.append(edit);
      }
      card.append(summary, details);
      detailHost = details;
    }
    const table = node("table");
    table.className = "so-table cr-ai-usage-table";
    if (consoleLayout) {
      const columns = node("colgroup");
      for (const width of [16, 16, 12, 8, 9, 16, 13, 10]) {
        const column = node("col");
        column.style.width = `${width}%`;
        columns.append(column);
      }
      table.append(columns);
    }
    const head = node("thead");
    const headRow = node("tr");
    for (const [index, label] of [
      "Person",
      "Customer",
      consoleLayout ? "Accepted AI charges" : "Acknowledged",
      "Requests",
      "Tokens",
      consoleLayout ? "Monthly AI spending cap" : "Person monthly limit",
      data.closed ? "Spend" : "Month-to-date spend",
      "Pending",
    ].entries()) {
      const heading = node("th", label);
      heading.scope = "col";
      if ([3, 4, 6, 7].includes(index)) heading.className = "cr-ai-number";
      headRow.append(heading);
    }
    head.append(headRow);
    const body = node("tbody");
    for (const person of data.people) {
      const row = node("tr");
      for (const [index, value] of [
        person.name,
        person.customerName,
        dateText(person.acknowledgedAt),
        String(person.requests),
        person.totalTokens,
        person.capDollars === undefined ? "Limit unavailable" : aiLimitWords(person.capDollars),
        money(person.amountCents),
        person.reservedCents === undefined ? "-" : money(person.reservedCents),
      ].entries()) {
        const cell = node("td", value);
        if ([3, 4, 6, 7].includes(index)) cell.className = "cr-ai-number";
        if (index === 2) cell.className = "cr-ai-date";
        if (
          consoleLayout &&
          index === 5 &&
          person.capDollars !== undefined &&
          person.capDollars !== null
        ) {
          const amount = node("span", `$${person.capDollars}`);
          amount.className = "cr-ai-money";
          const units = node("span", "per calendar month");
          units.className = "cr-ai-limit-units";
          cell.replaceChildren(amount, units);
        }
        row.append(cell);
      }
      body.append(row);
    }
    if (data.people.length === 0) {
      const row = node("tr");
      const empty = node("td", "No people to show.");
      empty.className = "cr-ai-usage-empty";
      empty.colSpan = 8;
      row.append(empty);
      body.append(row);
    }
    const foot = node("tfoot");
    const total = node("tr");
    const label = node("th", `${data.totalLabel ?? "Account total"}, ${data.people.length} people`);
    label.colSpan = 6;
    label.scope = "row";
    const totalAmount = node("td", money(data.totalCents));
    totalAmount.className = "cr-ai-number";
    total.append(label, totalAmount, node("td", ""));
    foot.append(total);
    table.append(head, body, foot);
    if (consoleLayout) {
      const tableScroll = node("div");
      tableScroll.className = "cr-ai-usage-scroll";
      tableScroll.tabIndex = 0;
      tableScroll.setAttribute("role", "region");
      tableScroll.setAttribute("aria-label", viewName);
      tableScroll.append(table);
      detailHost.append(tableScroll);
    } else card.append(table);
    if (data.closed) return;
    const caps = node("section");
    caps.className = "cr-ai-billing-limits";
    caps.append(node(consoleLayout ? "h5" : "h4", consoleLayout ? "Monthly AI spending cap" : "Monthly limits"));
    if (consoleLayout) {
      const capNote = node("p", AI_SPENDING_CAP_NOTE);
      capNote.className = "cr-ai-billing-note";
      caps.append(capNote);
    }
    if (consoleLayout) {
      const headings = node("div");
      headings.className = "cr-ai-cap-head";
      headings.setAttribute("aria-hidden", "true");
      for (const text of ["Name", "Used", "Pending", "Cap, dollars per month", ""])
        headings.append(node("span", text));
      caps.append(headings);
    }
    // AIBILL1 (DEC-596): the location's own limit is edited in the AI help section on this page, so it is not listed twice here.
    const listedCaps = consoleLayout ? data.caps : data.caps.filter((entry) => entry.scope !== "location");
    for (const cap of listedCaps) {
      const row = node("div");
      row.className = "cr-ai-cap-row";
      // The location's own page reads "Uptown, $0.00 used this month" with the input below (design/style-guide.md section 5).
      const name = node(
        "span",
        !consoleLayout && cap.usedCents !== null ? `${cap.name}, ${money(cap.usedCents)} used this month` : cap.name,
      );
      name.className = "cr-ai-cap-name";
      row.append(name);
      if (consoleLayout) {
        const used = node("span", cap.usedCents === null ? "-" : money(cap.usedCents));
        used.className = "cr-ai-cap-used";
        used.setAttribute(
          "aria-label",
          cap.usedCents === null ? "Used unavailable" : `${money(cap.usedCents)} used`,
        );
        const pending = node("span", cap.reservedCents === null ? "-" : money(cap.reservedCents));
        pending.className = "cr-ai-cap-pending";
        pending.setAttribute(
          "aria-label",
          cap.reservedCents === null
            ? "Pending unavailable"
            : `${money(cap.reservedCents)} pending`,
        );
        row.append(used, pending);
      } else {
        if (cap.reservedCents !== null && cap.reservedCents > 0)
          row.append(node("span", `${money(cap.reservedCents)} pending`));
      }
      if (!cap.editable) {
        const limit = node("span", aiLimitWords(cap.capDollars));
        limit.className = "cr-ai-cap-readonly";
        row.append(limit);
      } else {
        const form = node("form");
        form.className = "cr-ai-cap-form";
        const { field, input } = aiDollarInput(
          cap.capDollars,
          `${cap.name} monthly limit in dollars`,
        );
        const units = node("p", "Dollars per calendar month. Blank means No limit.");
        units.className = "admin-field-help";
        const save = node("button", "Save");
        save.type = "submit";
        if (consoleLayout) {
          save.className = "btn-primary";
          input.placeholder = "No limit";
          input.setAttribute("aria-description", units.textContent ?? "");
        }
        const error = node("span");
        error.setAttribute("role", "status");
        error.className = "cr-ai-cap-status";
        if (consoleLayout) form.append(field, save, error);
        else form.append(field, units, save, error);
        form.onsubmit = (event) => {
          event.preventDefault();
          const capDollars = input.value.trim();
          if (capDollars !== "" && !/^(0|[1-9][0-9]*)$/.test(capDollars)) {
            error.textContent = "Enter whole dollars.";
            return;
          }
          input.disabled = true;
          save.disabled = true;
          void host
            .saveCap({
              scope: cap.scope,
              scopeId: cap.scopeId,
              capDollars: capDollars === "" ? null : capDollars,
            })
            .then((reply) => {
              if (expected !== generation) return;
              if (reply.status === 200 || reply.status === 201) void load(data.month);
              else error.textContent = reply.body?.message ?? "Could not save the limit.";
            })
            .catch(() => {
              if (expected === generation) error.textContent = "Could not save the limit.";
            })
            .finally(() => {
              input.disabled = false;
              save.disabled = false;
            });
        };
        row.append(form);
      }
      caps.append(row);
    }
    if (listedCaps.length > 0) detailHost.append(caps);
  }
  return { load, reset };
}
